import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { isMobileUA, makeSequence } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const IS_MOBILE_UA = isMobileUA();
const IS_LT_MD = isLessThanBreakpoint('md');
const PUPPY_THUMBNAIL_SIZE = IS_LT_MD ? '240px' : '260px';

/** @param {any} props */
export function PuppyCardSkeleton({ imageHeight = null }) {
  return (
    <div
      className={classnames('PuppyCardSkeleton br3 overflow-hidden', {
        'ba b--default': IS_MOBILE_UA
      })}
    >
      <SkeletonBlock
        className="mb4 br3-ns"
        height={imageHeight || (IS_LT_MD ? 'calc(100vw - 48px)' : '335px')}
      />
      <div className={classnames({ 'ph4 pb4': IS_MOBILE_UA })}>
        <div className="flex items-center justify-between gap-24 mt5 mb2">
          <SkeletonBlock className="font-24" height="1em" width="6em" />
          <SkeletonBlock
            className="br-pill bg-passive font-12"
            height="2em"
            width="6.5em"
          />
        </div>
        <SkeletonBlock className="font-14" height="1em" width="8em" />
        <div className="flex items-center gap-8 bt b--default mt3 pt2">
          <SkeletonBlock className="br-100" width="48px" height="48px" />
          <div className="flex flex-column gap-4">
            <SkeletonBlock className="font-16" height="1em" width="8em" />
            <SkeletonBlock className="font-12" height="1em" width="12em" />
          </div>
        </div>
      </div>
    </div>
  );
}

PuppyCardSkeleton.propTypes = {
  imageHeight: PropTypes.string
};

/** @param {any} props */
function BreederCardSkeleton({ position = null }) {
  return (
    <div
      className={classnames(
        'BreederCardSkeleton flex flex-column justify-between bg-white h-100 b--default overflow-hidden pt5',
        IS_LT_MD && position !== 1 && 'bt',
        IS_LT_MD ? 'bb pv5 ph5' : 'br12 ba'
      )}
    >
      <div className="flex gap-4 pl5-md overflow-hidden nr6 nr0-md">
        {makeSequence(3).map(index => (
          <SkeletonBlock
            key={index}
            className="br3 w-100 flex-none"
            height={PUPPY_THUMBNAIL_SIZE}
            width={PUPPY_THUMBNAIL_SIZE}
          />
        ))}
      </div>
      <div className="flex flex-column gap-16 ph5-md pt4 pb5-md">
        <div className="flex justify-between">
          <div className="flex items-center gap-8">
            <SkeletonBlock className="br-100" height="40px" width="40px" />
            <div className="flex flex-column gap-4">
              <SkeletonBlock
                className="font-18 md:font-20"
                height="1em"
                width="10em"
              />
              <SkeletonBlock
                className="font-12"
                height="1em"
                width={IS_LT_MD ? '16em' : '24em'}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-48">
          <div className="flex-auto flex flex-column gap-4">
            {makeSequence(3).map(index => (
              <SkeletonBlock
                key={index}
                className="font-14"
                height="1em"
                width={index === 2 ? '50%' : '100%'}
              />
            ))}
          </div>
          {!IS_LT_MD && (
            <div className="flex flex-column items-center gap-8 br3 bg-gray-100 ba b--gray-200 pt4 pb3 ph4 nt7">
              <div
                className="ReviewsPhotos flex"
                style={{ '--image-size': '40px' }}
              >
                {makeSequence(3).map(index => (
                  <SkeletonBlock
                    key={index}
                    className={classnames(
                      'ReviewsPhotos__item ba b--white br2',
                      `ReviewsPhotos__item--${3 - index}`
                    )}
                  />
                ))}
              </div>
              <SkeletonBlock className="font-14" height="1em" width="8em" />
            </div>
          )}
        </div>
        {IS_LT_MD && (
          <div className="flex gap-8 items-center font-16 overflow-hidden pb1 nr6">
            {makeSequence(3).map(index => (
              <SkeletonBlock
                key={index}
                className="font-14 br-pill flex-shrink-0"
                height="2.2em"
                width={index === 1 ? '12em' : '9em'}
              />
            ))}
          </div>
        )}
        {!IS_LT_MD ? (
          <div className="flex gap-8 items-center font-16 overflow-hidden pb1 nr6 nt2">
            {makeSequence(3).map(index => (
              <SkeletonBlock
                key={index}
                className="font-14 br-pill flex-shrink-0"
                height="2.1em"
                width={index === 1 ? '12em' : '9em'}
              />
            ))}
          </div>
        ) : (
          <div className="FeaturedReview flex items-center justify-between gap-48 md:gap-16 font-12 br3 bg-gray-100 ba b--gray-200 pv3 ph3">
            <div className="flex-auto flex flex-column gap-4">
              <SkeletonBlock className="font-14 md:font-16" height="1em" />
              <SkeletonBlock className="font-14 md:font-16" height="1em" />
              <SkeletonBlock
                className="font-14 md:font-16"
                height="1em"
                width="8em"
              />
            </div>
            <div
              className="ReviewsPhotos flex"
              style={{ '--image-size': '40px' }}
            >
              {makeSequence(3).map(index => (
                <SkeletonBlock
                  key={index}
                  className={classnames(
                    'ReviewsPhotos__item ba b--white br2',
                    `ReviewsPhotos__item--${3 - index}`
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

BreederCardSkeleton.propTypes = {
  position: PropTypes.number
};

/** @param {any} props */
export default function SearchResultsSkeleton({
  className = null,
  resultsType,
  resultsPerPage,
  imageHeight = null
}) {
  const CardSkeleton =
    resultsType === RESULTS_TYPES.BREEDERS
      ? BreederCardSkeleton
      : PuppyCardSkeleton;

  const isPuppiesView = resultsType === RESULTS_TYPES.PUPPIES;

  return (
    <div className={classnames('SearchResultsSkeleton', className)}>
      <ul className="row">
        {makeSequence(resultsPerPage).map(index => (
          <li
            key={index}
            className={classnames(
              'mb10-md col-12 col-md-6 mb8-md',
              isPuppiesView ? 'col-xl-3 mb6' : 'mb2'
            )}
          >
            <CardSkeleton position={index + 1} imageHeight={imageHeight} />
          </li>
        ))}
      </ul>
    </div>
  );
}

SearchResultsSkeleton.propTypes = {
  className: PropTypes.string,
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
  resultsPerPage: PropTypes.number.isRequired,
  imageHeight: PropTypes.string
};
